import { infiniteQueryOptions, keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import type { ScopeRef } from "@/components/scope-picker/types";
import { encodeScope } from "@/lib/scope";
import { fetchResults } from "../strateg-api";
import type { ResultsQueryParams } from "../types";

const PAGE_SIZE = 100;

export type StrategResultsParams = Omit<ResultsQueryParams, "limit" | "offset" | "idfirm">;

/**
 * Buduje obiekt params do queryKey z luznych stanow UI. Wspoldzielony przez
 * strone (`useMemo` na zywych filtrach) i route loader (persisted snapshot),
 * zeby oba zrodla dawaly IDENTYCZNY hash queryKey (TanStack pomija pola
 * `undefined` przy hashowaniu).
 */
export function buildStrategResultsParams(input: {
	search?: string;
	categories?: string[];
	primaryCategoryCode?: string | null;
	tagId?: number | null;
	showExcluded?: boolean;
	priceAction?: string | null;
	mechanism?: string | null;
	centralSource?: string | null;
	ownPriceList?: boolean;
	sortBy?: string;
	sortDir?: "asc" | "desc";
}): StrategResultsParams {
	return {
		search: input.search || undefined,
		// Multi-select kategorii idzie do API jako pojedynczy string `NRF,OTC,RX`.
		// Pusta lista == brak filtra (undefined), backend zwraca wszystko.
		category:
			input.categories && input.categories.length > 0 ? input.categories.join(",") : undefined,
		primaryCategoryCode: input.primaryCategoryCode ?? undefined,
		tagId: input.tagId ?? undefined,
		showExcluded: input.showExcluded || undefined,
		priceAction: input.priceAction ?? undefined,
		mechanism: input.mechanism ?? undefined,
		sourceCode: input.centralSource ?? undefined,
		ownPriceList: input.ownPriceList || undefined,
		sortBy: input.sortBy,
		sortDir: input.sortDir,
	};
}

/**
 * Wspolne query options dla strony wynikow ORAZ prefetchu w route loaderze
 * (`wyniki.tsx`). Jedna fabryka = identyczny queryKey hash — loader z
 * `defaultPreload: "intent"` zaczyna pobieranie na hover linku w sidebarze,
 * a mount strony przejmuje dokladnie ten sam wpis cache zamiast strzelac
 * drugi raz.
 */
export function strategResultsInfiniteOptions(scopeKey: string, params: StrategResultsParams = {}) {
	return infiniteQueryOptions({
		queryKey: ["strateg-results", scopeKey, params],
		queryFn: ({ pageParam = 0 }) =>
			fetchResults({ ...params, scope: scopeKey, limit: PAGE_SIZE, offset: pageParam }),
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
			return loaded < lastPage.total ? loaded : undefined;
		},
		// keepPreviousData — przy zmianie filtra/apteki/kategorii pokazujemy stare dane,
		// dopoki nowe sie nie zaladuja. Bez tego StatisticsPanel/tabela odmontowuje sie
		// na chwile i widac irytujace miganie sekcji.
		placeholderData: keepPreviousData,
		staleTime: 5 * 60_000, // 5 min — SSE invaliduje gdy jest update
		gcTime: 10 * 60_000,
	});
}

export function useStrategResults(scope: ScopeRef, params: StrategResultsParams = {}) {
	return useInfiniteQuery(strategResultsInfiniteOptions(encodeScope(scope), params));
}
